import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import storePersist from "../../redux/storePersist";
import {
  ArrowRight,
  Award,
  Trophy,
  Medal,
  Building2,
  Network,
  GraduationCap,
  Handshake,
  Megaphone,
  Globe2,
  Users,
  Calendar,
  Sparkles,
  ShieldCheck,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  MapPin,
  Camera,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * ONE DIGIIT – Home Landing (Single-file React page)
 * Stack: React + TailwindCSS + framer-motion + lucide-react
 * Purpose: Unified landing page for ONE DIGIIT to showcase mission, five pillars, awards, partners,
 *          milestone gallery, and capture inbound leads with a strong CTA.
 */

// ================= SafeLink =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}
  const guestTokenmetaData = storePersist?.get("guestTokenMetadata")?.current?.result?.TokenMetadata?.CompanyName;

const SafeLink = ({ href, className = "", children, onClick = (e: any) => { } }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.location.hash = href;
    }
  };
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// ================= Shared UI =================
const Section = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);
const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white border border-slate-200 ${className}`}>{children}</div>
);
const H2 = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-amber-500">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white px-3 py-1 rounded-full">
    {children}
  </span>
);

// ================= Data (edit as needed) =================
const pillars = [
  { key: 'communities', name: 'Communities', icon: Globe2, color: 'from-emerald-500 to-teal-600', blurb: 'Grassroots DPI adoption via chapters, cohorts & guilds.' },
  { key: 'academy', name: 'Entrepreneurship Academy', icon: GraduationCap, color: 'from-indigo-600 to-violet-600', blurb: 'Curriculum, certifications, trainers, and job pathways.' },
  { key: 'cloud', name: 'DIGIIT.AI Cloud', icon: Building2, color: 'from-sky-500 to-cyan-600', blurb: 'ONDC/ULIP/OCEN/DSEP rails, seller/buyer apps, enterprise platforms.' },
  { key: 'catalyst', name: 'Catalyst', icon: Handshake, color: 'from-rose-500 to-fuchsia-600', blurb: 'Mentors, trainers, corporate linkages, CSR & impact.' },
  { key: 'partners', name: 'Partner Network', icon: Megaphone, color: 'from-amber-500 to-orange-600', blurb: 'Academia, Incubators, Advisors, NGOs, Influencers, Corporate.' },
];

const stats = [
  { label: 'Programs & DPIs', value: '150+' },
  { label: 'MSMEs Empowered', value: '10,000+' },
  { label: 'Communities & Chapters', value: '250+' },
  { label: 'Learners & Jobs', value: '20,000+' },
];

const awards = [
  { name: 'Best MSME Digital Platform (TN)', year: '2021', icon: Trophy },
  { name: 'ONDC Early Contributor', year: '2022', icon: Award },
  { name: 'ULIP MVP Cohort', year: '2022', icon: Medal },
  { name: 'Enabling Bharat 2.0 Launch', year: '2023', icon: Award },
];

const partners = [
  'Indian Bank', 'StartupTN', 'SIDBI', 'CII', 'IIMB', 'IITM', 'ULIP', 'ONDC', 'DSEP'
];

const moments = [
  { date: '2020-08', title: 'DIGIIT founded', tag: 'Milestone' },
  { date: '2021-09', title: 'TN MSME Award', tag: 'Award' },
  { date: '2022-04', title: 'ONDC Pilot Partner', tag: 'ONDC' },
  { date: '2022-05', title: 'ULIP MVP', tag: 'ULIP' },
  { date: '2023-04', title: 'Enabling Bharat 2.0', tag: 'Launch' },
  { date: '2024-09', title: 'Indian Bank Empanelment', tag: 'Partnership' },
];

const events = [
  { date: '2025-09-12', title: 'ONE DIGIIT Nation Clinic (Virtual)' },
  { date: '2025-09-20', title: 'Community e‑Marketplace Showcase (Coimbatore)' },
  { date: '2025-10-04', title: 'Women & DPI Summit (Chennai)' },
];

// ================= NAV =================
const Nav = () => {
    
  const submenus = [
    { href: '#pillars', label: 'Five Pillars' },
    { href: '#impact', label: 'Impact' },
    { href: '#credentials', label: 'Credentials' },
    { href: '#gallery', label: 'Moments' },
    { href: '#events', label: 'Events' },
    { href: '#contact', label: 'Contact' },
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
      <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
        {/* Second row: submenus (desktop only) */}
        <div className="hidden md:block flex flex-row w-full items-center justify-right">
          <Section
            id="nav-sub"
            className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
          >
            {submenus.map((i) => (
              <SafeLink
                key={i.href}
                href={i.href}
                className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
              >
                {i.label}
              </SafeLink>
            ))}

            {/* CTA */}
            <SafeLink
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600 shrink-0"
            >
              Talk to Us <ArrowRight className="w-4 h-4" />
            </SafeLink>
          </Section>
        </div>


        {/* Mobile menu button */}
        <button
          className="md:hidden p-2"
          onClick={() => setOpenMobile((v) => !v)}
          aria-label="Toggle menu"
        >
          <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
          <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
          <span className="block w-6 h-0.5 bg-slate-900"></span>
        </button>
      </Section>

      {/* Mobile dropdown */}
      {openMobile && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <Section id={''} className="py-4 flex flex-col gap-3">
            <div className="mt-4 border-t border-slate-200 pt-4">
              {submenus.map((i) => (
                <SafeLink
                  key={i.href}
                  href={i.href}
                  onClick={() => setOpenMobile(false)}
                  className="text-slate-600"
                >
                  {i.label}
                </SafeLink>
              ))}
            </div>
            <SafeLink
              href="#contact"
              onClick={() => setOpenMobile(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
            >
              Talk to Us <ArrowRight className="w-4 h-4" />
            </SafeLink>
          </Section>
        </div>
      )}
    </header>
  );
};

// ================= HERO =================
const Hero = () => (
  <div id="home" className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white">
    <div className="absolute inset-0 opacity-[0.1] pointer-events-none bg-[radial-gradient(600px_circle_at_20%_20%,#f59e0b_15%,transparent_60%),radial-gradient(500px_circle_at_85%_25%,#22c55e_12%,transparent_55%)]" />
    <Section id={'hero'} className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="flex gap-2 mb-4">
            <Pill><Sparkles className="w-4 h-4" /> DPI‑Led</Pill>
            <Pill><ShieldCheck className="w-4 h-4" /> Trust & Governance</Pill>
            <Pill><Users className="w-4 h-4" /> Inclusive Growth</Pill>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">ONE DIGIIT — Building India’s Digital Future</h1>
          <p className="mt-4 text-lg md:text-xl text-slate-200 max-w-2xl">A unified movement across five pillars — Communities, Academy, DIGIIT.AI Cloud, Catalyst, and Partner Network — to turn <b>learning into livelihood</b> and <b>platforms into prosperity</b>.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <SafeLink href="#contact" onClick={() => { }} className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-slate-900 px-5 py-3 font-semibold hover:bg-amber-400">Start a Conversation</SafeLink>
            <SafeLink href="#pillars" onClick={() => { }} className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Explore the Five Pillars</SafeLink>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> Weekly solution clinics</span>
            <span className="inline-flex items-center gap-2"><Award className="w-4 h-4" /> Recognized by Govt & Industry</span>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.05 }}>
          <Card className="p-6 md:p-8 bg-white/95 text-slate-900">
            <div className="font-semibold">Impact at a glance</div>
            <ul className="mt-4 grid grid-cols-2 gap-4">
              {stats.map(s => (
                <li key={s.label} className="rounded-xl p-4 border border-slate-200 text-center">
                  <div className="text-3xl font-black text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-600">{s.label}</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500">* Replace with live counters from your analytics when ready.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

// ================= PILLARS =================
const Pillars = () => (
  <Section id="pillars" className="py-16">
    <H2 kicker="Five Pillars" center>How ONE DIGIIT turns intent → outcomes</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {pillars.map(p => (
        <motion.div key={p.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
          <Card className="p-6 h-full flex flex-col">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${p.color} grid place-items-center text-white`}>
                {React.createElement(p.icon, { className: 'w-5 h-5' })}
              </div>
              <div className="text-xl font-bold text-slate-900">{p.name}</div>
            </div>
            <p className="mt-3 text-sm text-slate-700">{p.blurb}</p>
            <div className="mt-5 flex gap-3 mt-auto">
              <SafeLink href="#contact" onClick={() => { }} className="rounded-full px-4 py-2 text-sm font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400">Talk to us</SafeLink>
              <SafeLink href="#" onClick={() => { }} className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</SafeLink>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ================= CREDENTIALS (Awards & Recognitions) =================
const Credentials = () => (
  <Section id="credentials" className="py-16">
    <H2 kicker="Credentials" center>Awards & Recognitions</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {awards.map((a) => (
        <Card key={a.name} className="p-6 text-center">
          {React.createElement(a.icon, { className: 'w-10 h-10 text-amber-500 mx-auto' })}
          <div className="mt-3 font-semibold text-slate-900">{a.name}</div>
          <div className="text-sm text-slate-600">{a.year}</div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= PARTNERS =================
const Partners = () => (
  <Section id="partners" className="py-16">
    <H2 kicker="Ecosystem" center>Key Partnerships</H2>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {partners.map(name => (
        <div key={name} className="rounded-xl border border-slate-200 bg-white h-20 grid place-items-center text-sm font-semibold text-slate-600">
          {name}
        </div>
      ))}
    </div>
  </Section>
);

// ================= GALLERY / MOMENTS =================
const Gallery = () => (
  <Section id="gallery" className="py-16">
    <H2 kicker="Moments" center>Important Moments & Milestones</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {moments.map(m => (
        <motion.div key={m.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
          <Card className="overflow-hidden">
            {/* Placeholder visual block – replace with images/videos later */}
            <div className="bg-slate-200 h-40 grid place-items-center text-slate-500"><Camera className="w-8 h-8" /></div>
            <div className="p-5">
              <div className="text-xs text-slate-500">{m.date}</div>
              <div className="font-semibold text-slate-900">{m.title}</div>
              <div className="text-xs mt-1 inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full">{m.tag}</div>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ================= EVENTS =================
const Events = () => (
  <Section id="events" className="py-16">
    <H2 kicker="Events" center>Upcoming Clinics & Showcases</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {events.map(e => (
        <Card key={e.title} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Calendar className="w-4 h-4" /> {new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          <div className="mt-2 font-semibold text-slate-900">{e.title}</div>
          <div className="mt-3 flex gap-2">
            <SafeLink href="#contact" onClick={() => { }} className="rounded-full px-3 py-1.5 text-sm font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400">RSVP</SafeLink>
            <SafeLink href="#" onClick={() => { }} className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</SafeLink>
          </div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= CONTACT / LEAD CAPTURE =================
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", org: "", city: "", interest: "General", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => { e.preventDefault(); if (!form.name || !form.email) { alert('Please fill name & email'); return; } setSubmitted(true); };
    const navigate = useNavigate()
  return (
    <div id="contact" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section id={'contact'} className="py-16">
        <H2 kicker="Contact" center>Start a Conversation</H2>
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-2">
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input name="name" value={form.name} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Your name" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input name="email" type="email" value={form.email} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="+91‑" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Organization</label>
                <input name="org" value={form.org} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Your org / institution" />
              </div>
              <div>
                <label className="text-sm text-slate-600">City / Region</label>
                <input name="city" value={form.city} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Your city" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Interest</label>
                <select name="interest" value={form.interest} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500">
                  {["General", "Communities", "Academy", "DIGIIT.AI Cloud", "Catalyst", "Partner Network"].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Message</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Tell us about your goals" />
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-3 items-center">
                <button className="rounded-full bg-amber-500 text-slate-900 px-5 py-3 font-semibold hover:bg-amber-400" type="submit">Submit</button>
                {submitted && <span className="text-sm text-green-600 inline-flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Submitted! We will get back to you soon.</span>}
              </div>
            </form>
          </Card>
          <div className="space-y-4">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Reach us</div>
              <div className="mt-3 text-sm text-slate-600 flex flex-col gap-2">
                <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> hello@digiit.ai</span>
                <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> +91‑90000 00000</span>
                <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> Coimbatore • Chennai • Bengaluru</span>
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Looking to onboard your business with us?</div>
              <div className="mt-2 text-sm text-slate-600"><b>Register today and unlock new opportunities</b></div>
              <button onClick={() =>navigate('/venture/login')} className="mt-4 rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Click to Register</button>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ================= FOOTER =================
const Footer = () => (
  <footer className="bg-white border-t border-slate-200 text-slate-600">
    <Section id={'footer'} className="py-10">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
            <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-slate-900 text-white font-black">1</span>
            ONE DIGIIT
          </div>
          <p className="mt-3 text-sm text-slate-500 max-w-sm">A unified movement across five pillars to build India’s digital future.</p>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Quick Links</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#pillars" onClick={() => { }} className="hover:text-slate-900">Five Pillars</SafeLink></li>
            <li><SafeLink href="#credentials" onClick={() => { }} className="hover:text-slate-900">Credentials</SafeLink></li>
            <li><SafeLink href="#gallery" onClick={() => { }} className="hover:text-slate-900">Moments</SafeLink></li>
            <li><SafeLink href="#events" onClick={() => { }} className="hover:text-slate-900">Events</SafeLink></li>
            <li><SafeLink href="#contact" onClick={() => { }} className="hover:text-slate-900">Contact</SafeLink></li>
               <li><SafeLink href="#contact" 
               onClick={() => window.open(`https://${guestTokenmetaData}.xe.digiit.${process.env.REACT_APP_API_URL}`, '_blank')}
                className="hover:text-slate-900">IXE</SafeLink></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Legal</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#" onClick={() => { }} className="hover:text-slate-900">Terms</SafeLink></li>
            <li><SafeLink href="#" onClick={() => { }} className="hover:text-slate-900">Privacy</SafeLink></li>
          </ul>
        </div>
      </div>
      <div className="mt-8 border-t border-slate-200 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} ONE DIGIIT. All rights reserved.</div>
    </Section>
  </footer>
);

// ================= PAGE =================
export default function OneDigiitHomePage() {


  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav />
      <Hero />
      <Pillars />
      <Credentials />
      <Partners />
      <Gallery />
      <Events />
      <Contact />
      <Footer />
    </div>
  );
}
